import { Device, types as mediasoupTypes } from 'mediasoup-client';
import { Transport } from 'mediasoup-client/lib/Transport';
import { getCustomLogger } from '../../../../lib/logger';
import Signalling from './signalling';

const logger = getCustomLogger('screenapp:recorder:mediasoup');

class Mediasoup {
  private static instance: Mediasoup;

  private device: Device | null = null;

  private transport: Transport | null = null;

  private signalling: Signalling;

  private routerCapabilities: mediasoupTypes.RtpCapabilities;

  public static getInstance(): Mediasoup {
    if (!Mediasoup.instance) {
      Mediasoup.instance = new Mediasoup();
    }
    return Mediasoup.instance;
  }

  constructor() {
    this.signalling = new Signalling();
    this.device = new Device();
  }

  public async setup() {
    await this.signalling.connect();
    this.routerCapabilities = await this.signalling.getRouterCapabilities();
    await this.device?.load({ routerRtpCapabilities: this.routerCapabilities });
    if (!this.device?.canProduce('video')) {
      logger.error('Device cannot produce video');
      throw new Error('Device cannot produce video');
    }
    if (!this.device?.canProduce('audio')) {
      logger.error('Device cannot consume video');
      throw new Error('Device cannot consume video');
    }
    const routerTransportParams = await this.signalling.createTransport();
    this.transport = (await this.device?.createSendTransport(
      routerTransportParams
    )) as Transport;
    if (!this.transport) {
      logger.error('Failed to create transport');
      throw new Error('Failed to create transport');
    }
    this.transport.on(
      'connect',
      async ({ dtlsParameters }, callback, errback) => {
        try {
          if (!this.transport?.id) {
            logger.error('Transport ID not found');
            throw new Error('Transport ID not found');
          }
          await this.signalling.connectTransport({
            id: this.transport?.id,
            dtlsParameters,
          });
          callback();
        } catch (error) {
          errback(error);
        }
      }
    );

    this.transport.on(
      'produce',
      async ({ kind, rtpParameters, appData }, callback, errback) => {
        try {
          if (!this.transport?.id) {
            logger.error('Transport ID not found');
            throw new Error('Transport ID not found');
          }
          const { id } = await this.signalling.produce({
            id: this.transport?.id,
            kind,
            rtpParameters,
            appData,
          });
          callback({ id });
        } catch (error) {
          errback(error);
        }
      }
    );
  }

  public async addMediaStream(stream: MediaStream) {
    if (!this.transport) {
      logger.error('Transport not found');
      return;
    }

    try {
      const track = stream.getVideoTracks()[0];
      await this.transport?.produce({ track });
      logger.info('Media track added to transport');
    } catch (error) {
      logger.error('Error producing track', error);
    }
  }

  public close() {
    this.signalling.close();
  }
}

export default Mediasoup;
